/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Provider, useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { DataBlock, DataBlockSidebar, Line, Autocomplete } from 'tinybots-react-components'
import { Option, SideBarOption } from 'tinybots-react-components/lib/components/DashboardComponents/Analytics/types'
import { AppDispatch } from '../../../common/redux/store'
import { RootState } from '../../../common/redux/root.reducer'
import { dataGroupSelector, AnalyticsState, fetchDataBlock, fetchDataGroupDetailed, getGraphQLs, fetchOrganisations } from '../redux/reducer'
import StoreService from '../../../common/redux/store.service'
import { RelationDetailed } from '../../overview/types/RelationDetailed'

const Wrapper = styled.div.attrs({ id: 'data-group-wrapper' })`
  height: 100vh;
  width: 100%;
  display: flex;
  gap: 40px;
  padding: 20px;
  overflow: visible;
`

const DataBlockSidebarWrapper = styled.div`
  height: calc(100vh - 40px);
  flex-shrink: 0;
  position: sticky;
  top: 0;
  overflow-y: auto;
`

const DataBlockWrapper = styled.div`
  flex: 1;
  height: 100vh;
  overflow-y: auto;
  padding-top: 20px;
  padding-bottom: 80px;
  overflow-y: auto;
  margin-bottom: 40px;
`

const AutocompleteWrapper = styled.div`
  padding-bottom: 50px;
  width: 380px;
`

const OrganisationalTitleSection = styled.div`
  padding-bottom: 40px; 
  max-width: 1200px;
`

const getQueryParam = (key: string): string | null =>
  new URLSearchParams(window.location.search).get(key)

const getIntParam = (key: string): number | null => {
  const val = getQueryParam(key)
  const parsed = val ? parseInt(val, 10) : null
  return !isNaN(parsed) ? parsed : null
}

const setIdInQuery = (id: number | null, key: string) => {
  const url = new URL(window.location.href)
  id !== null ? url.searchParams.set(key, id.toString()) : url.searchParams.delete(key)
  window.history.replaceState({}, '', url.toString())
}

export interface DataGroupProps {
  $http: any
  StoreService: StoreService
}

const ConnectedDataGroup = (props: DataGroupProps) => {
  const dispatch: AppDispatch = useDispatch()
  const dataBlockWrapperRef = useRef<HTMLDivElement | null>(null)

  const view = useMemo(() => getQueryParam('type') ?? 'personal', [])
  const dataGroupId = useMemo(() => getIntParam('data-group-id') ?? 1, [])

  const [organisations, setOrganisations] = useState<Array<{key: number, label: string}>>([])
  const [dataBlockId, setDataBlockId] = useState<number | null>(getIntParam('data-block-id'))
  const [relationId, setRelationId] = useState<number | null>(getIntParam('relation-id'))
  const [selectedRelation, setSelectedRelation] = useState<Option | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<SideBarOption | null>(null)
  const [titleFilter, setTitleFilter] = useState('')
  const [filterValues, setFilterValues] = useState<Record<string, any>>({})
  const [filtersInitialized, setFiltersInitialized] = useState(false)
  const [displayBlocks, setDisplayBlocks] = useState([])

  const { graphQlData, loadingDataGroupDetailed: loadingDataGroup, loadingDataBlock, loadingGraphQL, dataGroupDetailed: dataGroup, dataBlock, organisations: rawOrganisationData } = useSelector<RootState, AnalyticsState>(dataGroupSelector)

  useEffect(() => {
    dispatch(fetchOrganisations())
  }, [])

  useEffect(() => {
    setOrganisations(rawOrganisationData.map((org: RelationDetailed) => ({
      key: org.id,
      label: org.name
    })))
  }, [rawOrganisationData])

  useEffect(() => {
    if (view === 'personal') {
      dispatch(fetchDataGroupDetailed({ dataGroupId }))
      dispatch(fetchDataBlock({ dataGroupId, dataBlockId }))
    } else {
      dispatch(fetchDataGroupDetailed({ relationId }))
      dispatch(fetchDataBlock({ relationId, dataBlockId }))
    }
  }, [view, dataGroupId, dispatch, relationId, dataBlockId])

  useEffect(() => {
    if (dataBlock) {
      const filterMap = dataBlock.filters?.reduce<Record<string, any>>((acc, filter) => {
        acc[filter.key] = filter.value ?? ''
        return acc
      }, {})
      setFilterValues(filterMap)
      setFiltersInitialized(true)
    } else {
      setFilterValues({})
      setFiltersInitialized(false)
    }
  }, [dataBlock])

  useEffect(() => {
    const r = organisations.filter((org) => org.key === relationId)
    setSelectedRelation(r[0])
  }, [relationId, organisations])

  useEffect(() => {
    if (dataBlock) {
      setDisplayBlocks(
        dataBlock.displayBlocks.map(block => ({
          ...block,
          onExportButtonClick: () => alert('Please let the product team know what you expect this button to do, so we can create the export functionality you want.'),
          data: [],
          loading: true
        }))
      )
    } else {
      setDisplayBlocks([])
    }
  }, [dataBlock])

  useEffect(() => {
    if (dataBlock && filtersInitialized) {
      dispatch(getGraphQLs({ displayBlocks: dataBlock.displayBlocks, filters: filterValues }))
    }
  }, [dataBlock, filterValues, filtersInitialized, relationId])

  useEffect(() => {
    if (graphQlData) {
      setDisplayBlocks(prev =>
        prev.map(block => {
          const found = graphQlData.find((d: {key: number, data: any}) => d?.key === block.id)
          return found ? { ...block, data: found.data } : block
        })
      )
    }
  }, [graphQlData])

  useEffect(() => {
    setDisplayBlocks(prev => prev.map(block => ({ ...block, loading: loadingGraphQL })))
  }, [loadingGraphQL])

  const categoryOptions = useMemo(() => {
    if (!dataGroup) return []
    const categories = Array.from(new Set(dataGroup.dataBlocks.map(opt => opt.category)))
    return categories.map((cat, i) => ({ key: i, label: cat }))
  }, [dataGroup])

  const getFilteredData = (category: SideBarOption | null, title: string) => {
    if (!dataGroup) return []
    return dataGroup.dataBlocks.filter(opt => {
      const matchesCategory = category ? opt.category === category.label : true
      const matchesTitle = title ? opt.title.toLowerCase().includes(title.toLowerCase()) : true
      return matchesCategory && matchesTitle
    })
  }

  const filteredSideBarData = useMemo(
    () => getFilteredData(selectedCategory, titleFilter),
    [dataGroup, selectedCategory, titleFilter]
  )

  const filtersWithHandlers = dataBlock?.filters?.filter(f => !f.hidden)
    .map(filter => ({
      ...filter,
      value: filterValues[filter.key],
      onChange: (val: string | number) =>
        setFilterValues(prev => ({ ...prev, [filter.key]: val }))
    }))

  const updateRelationId = (option: Option) => {
    const id = !isNaN(Number(option.key)) ? Number(option.key) : null
    setRelationId(id)
    setIdInQuery(id, 'relation-id')
  }

  const updateSelectedId = (id: number | null) => {
    setDataBlockId(id)
    setIdInQuery(id, 'data-block-id')
  }

  const handleCategoryChange = (category: SideBarOption | null) => {
    setSelectedCategory(category)
    const updated = getFilteredData(category, titleFilter)
    updateSelectedId(updated[0]?.id ?? null)
  }

  const handleTitleFilterChange = (title: string) => {
    setTitleFilter(title)
    const updated = getFilteredData(selectedCategory, title)
    updateSelectedId(updated[0]?.id ?? null)
  }

  return (
    <Wrapper>
      <DataBlockSidebarWrapper>
      <DataBlockSidebar
        title={dataGroup?.title}
        categoryOptions={categoryOptions}
        options={filteredSideBarData}
        selectedCategory={selectedCategory}
        onCategoryChange={handleCategoryChange}
        titleFilter={titleFilter}
        onTitleFilterChange={handleTitleFilterChange}
        selectedId={dataBlockId ?? -1}
        onSelectId={updateSelectedId}
        onBackClick={() => { window.location.href = `/data-group-overview?view=${view}` }}
        loading={loadingDataGroup}
      />
      </DataBlockSidebarWrapper>
      <DataBlockWrapper ref={dataBlockWrapperRef}>
        {view === 'organisational' &&
        <OrganisationalTitleSection>
          <AutocompleteWrapper>
            <Autocomplete
              options={organisations}
              value={selectedRelation}
              onChange={updateRelationId}
              placeholder="Search organisation"
            />
          </AutocompleteWrapper>
          <Line />
        </OrganisationalTitleSection>}
        <DataBlock
          title={dataBlock?.title}
          filters={filtersWithHandlers}
          displayBlocks={displayBlocks}
          hideDivider={view === 'organisational'}
          loading={loadingDataBlock}
        />
      </DataBlockWrapper>
    </Wrapper>
  )
}

const DataGroup: React.FC<DataGroupProps> = ({ StoreService, $http }) => (
  <Provider store={StoreService.store}>
    <ConnectedDataGroup $http={$http} StoreService={StoreService} />
  </Provider>
)

export default DataGroup
